import { RulerDimensionLine, Sigma } from "lucide-react";
import { useMemo } from "react";
import { NumericInput } from "../../components/NumericInput";
import { copy } from "../../data/i18n";
import { compactDecimalsFor, decimalsFor, toDisplayValue, unitFor } from "../../data/units";
import type { Language, UnitSystem } from "../../types/preferences";
import { formatNumber } from "../../utils/numberFormat";
import { calculateGeometry } from "./formulas";
import type { GeometryInputs, GeometryMode } from "./types";

type GeometryCalculatorProps = {
  language: Language;
  unitSystem: UnitSystem;
  mode: GeometryMode;
  inputs: GeometryInputs;
  inputText: Record<keyof GeometryInputs, string>;
  onModeChange: (mode: GeometryMode, calculatedKey?: keyof GeometryInputs, value?: number) => void;
  onInputTextChange: (key: keyof GeometryInputs, value: string) => void;
};

export function GeometryCalculator({
  language,
  unitSystem,
  mode,
  inputs,
  inputText,
  onModeChange,
  onInputTextChange
}: GeometryCalculatorProps) {
  const t = copy[language];
  const locale = language === "de" ? "de-DE" : "en-US";
  const result = useMemo(() => calculateGeometry(mode, inputs), [mode, inputs]);
  const calculatedKey: keyof GeometryInputs = mode === "zMeasure" ? "zMeasure" : "aMass";
  const heading = mode === "zMeasure" ? t.geometry.calculateZ : t.geometry.calculateA;
  const headingDescription = mode === "zMeasure" ? t.geometry.calculateZDescription : t.geometry.calculateADescription;
  const resultLabel = mode === "zMeasure" ? t.geometry.zMeasure : t.geometry.calculatedAMass;
  const resultDisplay = toDisplayValue("aMass", result.value, unitSystem);
  const resultDecimals = decimalsFor("aMass", unitSystem);
  const displayedInputs = {
    ...inputs,
    [calculatedKey]: result.value
  };

  function changeMode(nextMode: GeometryMode) {
    onModeChange(nextMode, calculatedKey, result.value);
  }

  return (
    <section className="calculator-panel" aria-label={t.modules.geometry}>
      <div className="calculator-heading">
        <div>
          <h2>{heading}</h2>
          <span>{headingDescription}</span>
        </div>
      </div>

      <div className="target-section">
        <div className="section-label">{t.geometry.targetSelection}</div>
        <div className="mode-selector geometry-mode-selector">
          <button
            type="button"
            className={mode === "zMeasure" ? "mode-button is-active" : "mode-button"}
            onClick={() => changeMode("zMeasure")}
          >
            <span>{t.geometry.calculateZ}</span>
          </button>
          <button
            type="button"
            className={mode === "aMass" ? "mode-button is-active" : "mode-button"}
            onClick={() => changeMode("aMass")}
          >
            <span>{t.geometry.calculateA}</span>
          </button>
        </div>
      </div>

      <div className="calculator-body conversion-body">
        <div className="parameter-section">
          <div className="section-label">{t.inputParameters}</div>
          <div className="input-grid">
            <NumericInput
              label={t.geometry.aMass}
              shortLabel="a"
              unit={unitFor("aMass", unitSystem)}
              value={toDisplayValue("aMass", displayedInputs.aMass, unitSystem)}
              textValue={inputText.aMass}
              decimals={decimalsFor("aMass", unitSystem)}
              locale={locale}
              disabled={mode === "aMass"}
              onChange={(value) => onInputTextChange("aMass", value)}
            />
            <NumericInput
              label={t.geometry.zMeasureInput}
              shortLabel="Z"
              unit={unitFor("aMass", unitSystem)}
              value={toDisplayValue("aMass", displayedInputs.zMeasure, unitSystem)}
              textValue={inputText.zMeasure}
              decimals={decimalsFor("aMass", unitSystem)}
              locale={locale}
              disabled={mode === "zMeasure"}
              onChange={(value) => onInputTextChange("zMeasure", value)}
            />
            <NumericInput
              label={t.geometry.angle}
              shortLabel={t.geometry.angleShort}
              unit={unitFor("angle", unitSystem)}
              value={inputs.angle}
              textValue={inputText.angle}
              decimals={decimalsFor("angle", unitSystem)}
              locale={locale}
              onChange={(value) => onInputTextChange("angle", value)}
            />
          </div>
        </div>

        <aside className="result-panel conversion-result" aria-live="polite">
          <div className="result-header">
            <span>
              <RulerDimensionLine size={16} /> {t.result}
            </span>
          </div>
          <p className="result-label">{resultLabel}</p>
          <div className="result-value">
            {formatNumber(resultDisplay, resultDecimals, locale)}
            <span>{unitFor("aMass", unitSystem)}</span>
          </div>

          <div className="formula-box">
            <span>
              <Sigma size={15} /> {t.formula}
            </span>
            <code>{mode === "zMeasure" ? "Z = a / cos(angle / 2)" : "a = Z * cos(angle / 2)"}</code>
          </div>

          <div className="detail-grid">
            <div>
              <span>{t.geometry.zMeasureFraction}</span>
              <strong>{result.zMeasureFraction}</strong>
            </div>
            <div>
              <span>{t.geometry.legFactor}</span>
              <strong>{formatNumber(result.legFactor, 3, locale)}</strong>
            </div>
            <div>
              <span>{t.geometry.halfAngle}</span>
              <strong>{formatNumber(inputs.angle / 2, compactDecimalsFor("angle", unitSystem, inputs.angle / 2), locale)} °</strong>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}
